import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import {
  CredentialForm,
  OAuthConnectButton,
} from '@cotera/client/app/components/tools';
import { emitToolCredentialUpdated } from '@cotera/client/app/etc/emit-tool-credential-updated';
import { CheckIcon, KeyIcon, LoaderIcon } from 'lucide-react';
import { PROVIDER_ICONS } from '@cotera/client/v0/components/tool-connections/constants';
import { useOwnerOrgId, type ToolRendererProps } from './tool-renderer-props';
import { useToolCredentialInfo } from '@cotera/client/v0/components/tool-connections/hooks';
import type { HijackerWizardContext } from '../input-hijackers/types';
import { Button } from '../../components/ui/button';
import { Icon, Loading } from '@cotera/client/app/components/ui';
import { useSuspensfulToolInfo } from '@cotera/client/app/hooks/use-tools-info';
import { useAgentDisplayNameResolver } from '@cotera/client/app/hooks/use-agents-display-names';
import { useAction, useActionsContext } from '../../actions/framework';
import { Message, MessageContent } from '../../components/ai-elements/message';
import { submitCredentialsAction } from './submit-credentials.action';
import {
  useAnalytics,
  useChatSurface,
  CocoEvent,
} from '@cotera/client/app/etc';
import { RunningToolTitle } from '@cotera/client/v0/chat/running-tool-title';

// Import SubmitToolAction from v1 - this will be tree-shaken if not used
import { SubmitToolAction as CocoSubmitToolAction } from '@cotera/client/app/v1/chat/actions/coco-submit-tool.action';
import {
  getCredentialStrategyOauthProvider,
  isCredentialStrategyOauthConnect,
} from '@cotera/api';
import type {
  CredentialStrategyInfo,
  ToolCredentialInfoResponse,
} from '@cotera/api';

export type { CredentialStrategyInfo } from '@cotera/api';

type AskForCredentialsInput = {
  toolKey: string;
};

export type AskForCredentialsOutput = {
  success: boolean;
  message: string;
};

export type CredentialInfo = Pick<
  ToolCredentialInfoResponse,
  'provider' | 'displayName' | 'strategies'
>;

/**
 * Hook to detect if we're running in v1/coco mode.
 * Checks for the presence of CocoChatModel-specific properties in the context.
 */
function useIsCocoMode(): boolean {
  const context = useActionsContext();
  const models = context.getAllInScopeModels();
  return models.some(
    (m) =>
      'resourceType' in m &&
      m.resourceType === 'chat' &&
      'conversationWatchable' in m
  );
}

/**
 * Hook that returns the appropriate tool submission function based on the mode.
 * In v0: uses SubmitToolAction (calls submitTool endpoint)
 * In v1/coco: uses RunToolAction (calls runTool endpoint with approve action)
 */
function useToolSubmitter(chatModel: ToolRendererProps['chatModel']): {
  submitToolResult: (params: {
    toolCallId: string;
    conversationId: string;
    credentialInfo: CredentialInfo;
    success: boolean;
    message: string;
  }) => Promise<void>;
} {
  const isCocoMode = useIsCocoMode();
  const client = useTenantedClient();
  const cocoSubmitToolAction = useAction(CocoSubmitToolAction);

  const submitToolResult = useCallback(
    async (params: {
      toolCallId: string;
      conversationId: string;
      credentialInfo: CredentialInfo;
      success: boolean;
      message: string;
    }): Promise<void> => {
      if (isCocoMode) {
        // In v1/coco mode: use SubmitToolAction (from v1) with the result
        const result = await cocoSubmitToolAction.execute({
          toolCallId: params.toolCallId,
          conversationId: params.conversationId,
          result: {
            credentialInfo: params.credentialInfo,
            success: params.success,
            message: params.message,
          },
        });

        if (result.isErr()) {
          const error = result.error[0] as
            | { t: string; message?: string }
            | undefined;
          throw new Error(
            error?.t === 'error'
              ? error.message ?? 'Failed to submit tool result'
              : 'Failed to submit tool result'
          );
        }
      } else {
        // In v0 mode: use submitCredentialsAction directly
        const result = await submitCredentialsAction({
          toolCallId: params.toolCallId,
          toolKey: '', // Not used in the submission
          credentialInfo: params.credentialInfo,
          successMessage: params.message,
          success: params.success,
          conversation: null, // Not used anymore
          client,
          conversationId: params.conversationId,
          model: chatModel.model,
        });

        if (result.isErr()) {
          throw new Error(result.error);
        }
      }
    },
    [isCocoMode, client, cocoSubmitToolAction, chatModel.model]
  );

  return { submitToolResult };
}

export const useToolName = (toolKey: string | undefined) => {
  const agentName = useAgentDisplayNameResolver();
  const toolInfo = useSuspensfulToolInfo(toolKey ?? '');
  return toolKey ? agentName(toolKey) ?? toolInfo?.name ?? toolKey : undefined;
};

export const AskForCredentialsMessageRenderer: React.FC<ToolRendererProps> = ({
  part,
}) => {
  const toolKey = part.input?.['toolKey'] as string | undefined;
  // Check if we have a successful result in the part output
  const output = part.getOutput<AskForCredentialsOutput>();
  const toolName = useToolName(toolKey);
  const hasSuccessResult =
    part.isCompleted() && output !== undefined && output.success === true;

  if (hasSuccessResult) {
    return (
      <div className="flex items-center gap-1.5 text-xs italic text-muted-foreground py-1">
        <CheckIcon className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
        <span>Credentials for {toolName} configured successfully.</span>
      </div>
    );
  }

  if (part.isCompleted() && output !== undefined && output.success === false) {
    return (
      <div className="flex items-center gap-1.5 text-xs italic text-muted-foreground py-1">
        <Icon
          icon="x-circle"
          className="h-3 w-3 text-red-600 dark:text-red-400"
        />
        <span>Credentials for {toolName} not configured.</span>
      </div>
    );
  }

  return null;
};

/**
 * Renderer for coco/ask-for-credentials.
 * Shows a credentials form for the tool that needs to be configured.
 */
export const AskForCredentialsRenderer: React.FC<
  ToolRendererProps & {
    wizard?: HijackerWizardContext;
    conversationId?: string;
  }
> = ({ part, chatModel, readonly, wizard, conversationId }) => {
  const input = part.input as AskForCredentialsInput | undefined;
  const toolKey = input?.toolKey ?? '';

  if (!toolKey) {
    return (
      <div className="text-sm text-muted-foreground py-1">
        No tool key provided
      </div>
    );
  }

  // Check if we have a successful result in the part output
  const output = part.getOutput<AskForCredentialsOutput>();
  const hasSuccessResult =
    part.isCompleted() && output !== undefined && output.success !== false;

  return (
    <Suspense
      fallback={
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground py-1">
          <LoaderIcon className="h-3 w-3 animate-spin" />
          <RunningToolTitle className="italic text-xs">
            Checking connection...
          </RunningToolTitle>
        </div>
      }
    >
      <AskForCredentialsContent
        toolKey={toolKey}
        toolCallId={part.toolCallId}
        chatModel={chatModel}
        readonly={readonly ?? false}
        hasSuccessResult={hasSuccessResult}
        toolName={
          output?.message
            ? extractToolNameFromMessage(output.message)
            : undefined
        }
        wizard={wizard}
        conversationId={conversationId ?? chatModel.id}
      />
    </Suspense>
  );
};

/**
 * Extracts tool name from the success message if available.
 * Falls back to undefined if not found.
 */
/**
 * Shown when the user is from another org and cannot configure credentials.
 * Only the owning org can add credentials; others see this waiting state.
 */
export const WaitingForCredentials: React.FC<{ toolName?: string }> = () => (
  <Message from="assistant">
    <MessageContent>
      <div className="flex items-center gap-2">
        <span>Waiting for credentials to be configured...</span>
      </div>
    </MessageContent>
  </Message>
);

function extractToolNameFromMessage(message: string): string | undefined {
  // The message format is typically: "Credentials configured successfully for {toolName}"
  // or "Credentials request for "{toolName}" processed. Please configure credentials to continue."
  const match = message.match(/for\s+"?([^"]+)"?/);
  return match ? match[1] : undefined;
}

const AskForCredentialsContent: React.FC<{
  toolKey: string;
  toolCallId: string;
  chatModel: ToolRendererProps['chatModel'];
  readonly: boolean;
  hasSuccessResult: boolean;
  toolName?: string;
  wizard?: HijackerWizardContext;
  conversationId: string;
}> = ({
  toolKey,
  toolCallId,
  chatModel,
  readonly,
  hasSuccessResult,
  wizard,
  conversationId,
}) => {
  const { data: toolInfo } = useToolCredentialInfo(toolKey);
  const userOrgId = useWhoami((s) => s.org.id);
  const ownerOrgId = useOwnerOrgId(chatModel);

  const isUserFromOwningOrg =
    ownerOrgId === undefined || ownerOrgId === userOrgId;

  // If we have a success result, show success state immediately
  if (hasSuccessResult) {
    return null;
  }

  if (!isUserFromOwningOrg) {
    return (
      <WaitingForCredentials toolName={toolInfo?.displayName ?? toolKey} />
    );
  }

  if (
    toolInfo === null ||
    toolInfo === undefined ||
    toolInfo.strategies.length === 0
  ) {
    return (
      <div className="text-sm text-muted-foreground py-1">
        No credential strategy available for {toolKey}
      </div>
    );
  }

  const rawStrategy = toolInfo.strategies[0]!;
  const strategy: CredentialStrategyInfo = rawStrategy;

  const credentialInfo: CredentialInfo = {
    provider: toolInfo.provider,
    displayName: toolInfo.displayName,
    strategies: [strategy],
  };

  return (
    <CredentialFormCard
      credentialInfo={credentialInfo}
      strategy={strategy}
      toolKey={toolKey}
      toolName={toolInfo.displayName}
      toolCallId={toolCallId}
      chatModel={chatModel}
      readonly={readonly}
      wizard={wizard}
      conversationId={conversationId}
    />
  );
};

const useSubmitCredentials = (
  toolCallId: string,
  toolName: string,
  strategy: CredentialStrategyInfo,
  credentialInfo: CredentialInfo,
  toolKey: string,
  conversationId: string,
  chatModel: ToolRendererProps['chatModel']
) => {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  const analytics = useAnalytics();
  const surface = useChatSurface() ?? 'scratch';
  const { submitToolResult } = useToolSubmitter(chatModel);

  const submitToolCompletion = async () => {
    await submitToolResult({
      toolCallId,
      conversationId,
      credentialInfo,
      success: true,
      message: `Credentials configured successfully for ${toolName}`,
    });
  };

  const submitCredentials = useMutation({
    mutationFn: async (credentials: Record<string, string>) => {
      const result = await client.toolsV2.createProviderAccount({
        toolKey,
        strategyName: strategy.strategy.name,
        credentials,
      });
      if (result.isErr()) {
        const errorObj = result.error.err;
        throw new Error(
          'message' in errorObj && errorObj.message
            ? errorObj.message
            : 'Failed to save credentials'
        );
      }
      return result.value;
    },
    onSuccess: async () => {
      analytics.track(CocoEvent.ToolCredentialsSubmitted, {
        conversationId,
        surface,
        toolKey,
        providerKey: credentialInfo.provider,
        strategyType: strategy.strategy.t,
      });
      // Broadcast the new credential; the tool-credential-updated query-sync
      // watchable refetches the tool caches (propagating into @-mentions etc).
      emitToolCredentialUpdated(orgId, credentialInfo.provider);
      await submitToolCompletion();
    },
  });

  return {
    submitCredentials,
    submitToolCompletion,
  };
};

const SubmitToolButton: React.FC<{
  credentials: Record<string, string>;
  toolCallId: string;
  toolName: string;
  chatModel: ToolRendererProps['chatModel'];
  strategy: CredentialStrategyInfo;
  toolKey: string;
  isOAuthStrategy: boolean;
  credentialInfo: CredentialInfo;
  conversationId: string;
}> = ({
  credentials,
  toolCallId,
  toolName,
  chatModel,
  strategy,
  toolKey,
  isOAuthStrategy,
  credentialInfo,
  conversationId,
}) => {
  const { submitCredentials, submitToolCompletion } = useSubmitCredentials(
    toolCallId,
    toolName,
    strategy,
    credentialInfo,
    toolKey,
    conversationId,
    chatModel
  );
  if (isOAuthStrategy) {
    return (
      <Suspense fallback={<Loading.Spinner variant="sm" />}>
        <OAuthConnectButton
          displayName={strategy.displayName}
          provider={getCredentialStrategyOauthProvider(strategy.strategy)}
          onConnected={async () => {
            await submitToolCompletion();
          }}
        />
      </Suspense>
    );
  }
  return (
    <Button
      size={'sm'}
      type="button"
      disabled={submitCredentials.isPending}
      onClick={async () => {
        await submitCredentials.mutateAsync(credentials);
      }}
    >
      {submitCredentials.isPending ? (
        <Loading.Spinner variant="sm" />
      ) : (
        'Connect'
      )}
    </Button>
  );
};

const useSkipCredentials = (
  toolCallId: string,
  toolName: string,
  credentialInfo: CredentialInfo,
  _toolKey: string,
  conversationId: string,
  chatModel: ToolRendererProps['chatModel']
) => {
  const { submitToolResult } = useToolSubmitter(chatModel);

  const submitSkip = async () => {
    await submitToolResult({
      toolCallId,
      conversationId,
      credentialInfo,
      success: false,
      message: `User declined to configure credentials for ${toolName}`,
    });
  };

  return { submitSkip };
};

const SkipToolButton: React.FC<{
  credentialInfo: CredentialInfo;
  toolCallId: string;
  toolName: string;
  chatModel: ToolRendererProps['chatModel'];
  strategy: CredentialStrategyInfo;
  toolKey: string;
  isOAuthStrategy: boolean;
  conversationId: string;
}> = ({
  credentialInfo,
  toolCallId,
  toolName,
  chatModel,
  toolKey,
  conversationId,
}) => {
  const { submitSkip } = useSkipCredentials(
    toolCallId,
    toolName,
    credentialInfo,
    toolKey,
    conversationId,
    chatModel
  );

  return (
    <Button
      size={'sm'}
      variant="outline"
      type="button"
      onClick={async () => {
        await submitSkip();
      }}
    >
      <Icon icon="skip" /> Skip
    </Button>
  );
};

/**
 * Form for configuring credentials.
 * On submit: saves credentials via secure endpoint, then executes the tool call via API.
 * Exported for use by LoadToolRenderer when load-tool returns incomplete with requiresCredentials.
 */
export const CredentialFormCard: React.FC<{
  credentialInfo: CredentialInfo;
  strategy: CredentialStrategyInfo;
  toolKey: string;
  toolName: string;
  toolCallId: string;
  chatModel: ToolRendererProps['chatModel'];
  readonly: boolean;
  wizard?: HijackerWizardContext;
  conversationId: string;
}> = ({
  credentialInfo,
  strategy,
  toolKey,
  toolName,
  toolCallId,
  chatModel,
  readonly,
  wizard,
  conversationId,
}) => {
  const [hasInput, setHasInput] = useState(false);
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const ownerOrgId = useOwnerOrgId(chatModel);
  const orgId = useWhoami((s) => s.org.id);
  const isUserFromOwningOrg = ownerOrgId === undefined || ownerOrgId === orgId;

  // Update wizard's canComplete based on form input state
  useEffect(() => {
    if (wizard) {
      // For OAuth, we can't really validate input, so enable completion
      // For form-based, require some input
      const isOAuth = isCredentialStrategyOauthConnect(strategy.strategy);
      wizard.setCanComplete(isOAuth || hasInput);
    }
  }, [wizard, hasInput, strategy]);

  // Handle wizard buttons
  useEffect(() => {
    if (!wizard) {
      return;
    }

    wizard.registerSkipButton(
      <SkipToolButton
        credentialInfo={credentialInfo}
        toolCallId={toolCallId}
        toolName={toolName}
        chatModel={chatModel}
        strategy={strategy}
        toolKey={toolKey}
        isOAuthStrategy={isCredentialStrategyOauthConnect(strategy.strategy)}
        conversationId={conversationId}
      />
    );

    wizard.registerSubmitButton(
      <SubmitToolButton
        credentials={credentials}
        credentialInfo={credentialInfo}
        toolCallId={toolCallId}
        toolName={toolName}
        chatModel={chatModel}
        strategy={strategy}
        toolKey={toolKey}
        isOAuthStrategy={isCredentialStrategyOauthConnect(strategy.strategy)}
        conversationId={conversationId}
      />
    );

    return () => {
      // Unregister on cleanup
      wizard.registerSkipButton(null);
      wizard.registerSubmitButton(null);
    };
  }, [
    wizard,
    toolCallId,
    chatModel,
    toolName,
    credentialInfo,
    strategy,
    credentials,
    toolKey,
    conversationId,
  ]);

  const handleFormChange = (values: Record<string, string>) => {
    const hasAnyValue = Object.values(values).some((v) => v.trim().length > 0);
    setCredentials(values);
    setHasInput(hasAnyValue);
  };

  if (!isUserFromOwningOrg) {
    return <WaitingForCredentials toolName={toolName} />;
  }

  const ProviderIcon = PROVIDER_ICONS[credentialInfo.provider];
  const isOAuthStrategy = isCredentialStrategyOauthConnect(strategy.strategy);

  return (
    <div className="p-2 pb-4">
      {isOAuthStrategy ? (
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            {ProviderIcon !== undefined ? (
              <span className="h-5 w-5 flex items-center justify-center [&>svg]:h-5 [&>svg]:w-5">
                <ProviderIcon />
              </span>
            ) : (
              <KeyIcon className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="font-medium text-sm">{strategy.displayName}</span>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 mb-3">
            {ProviderIcon !== undefined ? (
              <span className="h-5 w-5 flex items-center justify-center [&>svg]:h-5 [&>svg]:w-5">
                <ProviderIcon />
              </span>
            ) : (
              <KeyIcon className="h-4 w-4 text-muted-foreground" />
            )}
            <span className="font-medium text-sm">
              Configure {credentialInfo.displayName}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {strategy.description}
          </p>
          {!readonly && (
            <CredentialForm
              provider={credentialInfo.provider}
              strategyName={strategy.strategy.name}
              fields={strategy.credentialFields}
              variant="compact"
              submitLabel="Connect"
              showDocsLink={false}
              onChange={handleFormChange}
            />
          )}
        </>
      )}
    </div>
  );
};
